import { z } from 'zod';

/** Expiry status derived server-side relative to today (+ the expiring window). */
export type BatchStatus = 'ok' | 'expiring' | 'expired' | 'none';

export interface ItemBatch {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  warehouseId: string | null;
  warehouseName: string | null;
  batchNumber: string;
  expiryDate: string | null;
  receivedDate: string | null;
  quantity: string;
  cost: string | null;
  costCurrencyCode: string | null;
  note: string | null;
  status: BatchStatus;
  /** Days until expiry (negative when already expired); null when no expiry set. */
  daysToExpiry: number | null;
  createdAt: string;
}

export interface BatchSummary {
  /** Window (days) used to flag "expiring soon". */
  expiringWindowDays: number;
  expiredCount: number;
  expiringCount: number;
  totalCount: number;
}

const qty = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative amount');
const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');

export const BatchCreateSchema = z.object({
  itemId: z.string().uuid(),
  warehouseId: z.string().uuid().nullable().optional(),
  batchNumber: z.string().min(1).max(64),
  expiryDate: dateStr.nullable().optional(),
  receivedDate: dateStr.nullable().optional(),
  quantity: qty,
  cost: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  costCurrencyCode: z.string().length(3).regex(/^[A-Z]{3}$/).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export type BatchCreate = z.infer<typeof BatchCreateSchema>;

export const BatchUpdateSchema = z.object({
  batchNumber: z.string().min(1).max(64).optional(),
  warehouseId: z.string().uuid().nullable().optional(),
  expiryDate: dateStr.nullable().optional(),
  receivedDate: dateStr.nullable().optional(),
  quantity: qty.optional(),
  cost: z.string().regex(/^\d+(\.\d{1,4})?$/).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});
export type BatchUpdate = z.infer<typeof BatchUpdateSchema>;
